import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { userTypeTitleFromReaderTypes } from "../utils";
import { NOTICEBOARD, CLIENT_NOTICEBOARD } from "src/constants/permissions";
import { FILTER_NOTICE } from "./../Noticeboard";
import NoticeboardCard from "./NoticeboardCard";
import { Button, CardTableCell, CardTableCellHeader, CardTableRow } from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import AttachIcon from "src/img/new/attach-image.svg";
import MeLogo from "src/img/new/me_logo.svg";
import FacebookIcon from "src/img/noticeboard/facebook_colored.svg";
import InstagramIcon from "src/img/noticeboard/instagram.svg";
import LinkedInIcon from "src/img/noticeboard/linkedin_colored.svg";
import { SocialMediaPlatform } from "./AccountLinking/utils/Enums";
import CarouselTableView from "./CarouselTableView";
import { LottieLoadingIndicator } from "src/components";
import { getAssetUrl } from "../utils";

// const maxImageSize = 4194304; // 4 MB

const NoticeboardRow = styled(CardTableRow).attrs({
    columns: "2.25rem minmax(8rem, 0.75fr) minmax(12rem, 1fr) minmax(16rem, 1.35fr) minmax(14rem, 1.1fr) minmax(10rem, 0.85fr)",
    minWidth: "1040px"
})``;

const DateLabel = styled.div`
    text-align: left;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: var(--calendar-text, #171A22);
    opacity: 1;
`;

const PostActionButton = styled(Button)`
    border-radius: 8px !important;
    color: var(--calendar-text, #171A22);
    background-color: var(--calendar-surface, #ffffff);
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 700;
    text-align: center;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--calendar-border-strong, #C9D2E7);
    height: 36px;
    width: 7rem;
`;

const AttachmentHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
`;

const PostedToIcons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    flex-grow: 1;
    gap: 8px;
`;

const PreviewPlaceholderIcon = styled.img`
    width: 5.25rem;
    height: 5.25rem;
    object-fit: contain;
`;

const PreviewMedia = styled.img`
    width: 100%;
    height: 100%;
    padding: 0;
    object-fit: contain;
    object-position: center;
`;

const PreviewVideo = styled.video`
    width: 100%;
    height: 100%;
    padding: 0;
    object-fit: contain;
    object-position: center;
`;

const ProcessingPreview = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 140px;
    background-color: #f5f5f5;
    color: #888;
    font-size: 14px;
    font-style: italic;
    border: 1px dashed #ccc;
    padding: 10px;
    text-align: center;
    gap: 8px;
    box-sizing: border-box;
`;

const DatePostedLabel = ({ isActive }) => {
    if (isActive) {
        return <div>Date Posted</div>;
    } else {
        return <div>Scheduled</div>;
    }
};

const DatePostedContent = ({ notice }) => {
    const isScheduled = Boolean(notice.scheduleDate);
    let localDate = isScheduled ?
        moment.utc(notice.scheduleDateUtc).local() :
        moment.utc(notice.postedDate).local();

    if (!isScheduled && notice.postedDate === null) {
        localDate = moment.utc(notice.created_at).local();
    }

    return (
        <div>
            {localDate.format("Do MMMM YYYY")}
            <br />
            {localDate.format("h:mm A")}
        </div>
    );
};

function NoticeboardContent({ user, filterByUserTypeOption, noticeArray, fetching, checkedNotices, handleCheckboxChange, handleEditNotice, handleShowNoticeViewershipModal }) {
    const userHasClientManagement = user.permissions.includes(CLIENT_NOTICEBOARD);
    const userHasNoticeboardPermission = user.permissions.includes(NOTICEBOARD);
    const isEmployeesNoticeboard = filterByUserTypeOption === FILTER_NOTICE.EMPLOYEE;
    const userHasPrivileges = (isEmployeesNoticeboard && userHasNoticeboardPermission) || (!isEmployeesNoticeboard && userHasClientManagement);

    const [carouselIndices, setCarouselIndices] = useState({});

    const handleCarouselIndexChange = (id, newIndex) => {
        setCarouselIndices((prev) => ({
            ...prev,
            [id]: newIndex,
        }));
    };

    return noticeArray.map((notice, index) => {
        const currentIndex = carouselIndices[notice.uuid] || 1;
        const isActive = checkedNotices.includes(notice.uuid);
        const isScheduleDateFuture = moment(notice.scheduleDate).isAfter(moment());
        const postedLabel = !notice.postedDate && isScheduleDateFuture ? "Posting To" : "Posted To";
        const postedToEntries = notice.postedTo && notice.postedTo.reduce((acc, current) => {
            const exists = acc.find(item => item.socialMediaType === current.socialMediaType);
            if (!exists) {
                acc.push(current);
            }
            return acc;
        }, []);

        return (
            <NoticeboardRow key={notice.uuid || index} selected={isActive}>
                <CardTableCell>
                    <CardTableCellHeader>
                        <label>
                            <Checkbox
                                readOnly={false}
                                checked={isActive}
                                onChange={() => handleCheckboxChange(notice.uuid)}
                            />
                        </label>
                    </CardTableCellHeader>
                </CardTableCell>
                <CardTableCell>
                    <CardTableCellHeader>
                        <DatePostedLabel isActive={notice.isActive} />
                    </CardTableCellHeader>
                    <div style={{ display: "flex", height: "80%", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <DateLabel>
                            <DatePostedContent notice={notice} />
                        </DateLabel>
                        {userHasPrivileges &&
                                <PostActionButton onClick={() => handleEditNotice(notice)} >Edit Post</PostActionButton>
                        }
                    </div>
                </CardTableCell>
                <CardTableCell>
                    <CardTableCellHeader>
                        <div>Subject</div>
                    </CardTableCellHeader>
                    <NoticeboardCard content={notice.subject} loading={fetching} ></NoticeboardCard>
                </CardTableCell>
                <CardTableCell>
                    <CardTableCellHeader>
                        <div>Message</div>
                    </CardTableCellHeader>
                    <NoticeboardCard content={notice.message} loading={fetching} ></NoticeboardCard>
                </CardTableCell>
                <CardTableCell>
                    <CardTableCellHeader>
                        <AttachmentHeader>
                            <div>{notice.postType === "text" ? "No Attachment" : (notice.postType === "image" ? "Image" : notice.postType === "video" ? "Video" : "Carousel")}</div>
                            {notice?.postType === "carousel" && <div style={{ fontWeight: "normal" }}>{`${currentIndex} of ${notice?.carousel?.length}`}</div>}
                        </AttachmentHeader>
                    </CardTableCellHeader>
                    <NoticeboardCard content={""} cardType="preview" loading={fetching} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <PostTypeAssetPreview notice={notice} handleCarouselIndexChange={handleCarouselIndexChange} />
                    </NoticeboardCard>
                </CardTableCell>


                <CardTableCell>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        {userHasPrivileges && <>
                            <CardTableCellHeader>{userTypeTitleFromReaderTypes(notice)} Views</CardTableCellHeader>
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
                                <PostActionButton onClick={() => handleShowNoticeViewershipModal(notice)} >{notice.readCount} Views</PostActionButton>
                            </div>
                        </>}

                        <CardTableCellHeader>{postedLabel}</CardTableCellHeader>
                        <PostedToIcons>
                            <div style={{ width: "25px", height: "25px", backgroundColor: "#000", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <img src={MeLogo} alt="Me Business App logo" style={{ width: "15px", height: "15px" }} />
                            </div>
                            {
                                postedToEntries.map((postedItem, index) => {
                                    switch (postedItem.socialMediaType) {
                                            case SocialMediaPlatform.FACEBOOK:
                                                return (
                                                    <div key={index}>
                                                        <img src={FacebookIcon} alt="Facebook Icon" style={{ width: "25px", height: "25px" }} />
                                                    </div>
                                                );
                                            case SocialMediaPlatform.INSTAGRAM:
                                                return (
                                                    <div key={index}>
                                                        <img src={InstagramIcon} alt="Instagram Icon" style={{ width: "25px", height: "25px" }} />
                                                    </div>
                                                );
                                            case SocialMediaPlatform.LINKEDIN:
                                                return (
                                                    <div key={index}>
                                                        <img src={LinkedInIcon} alt="LinkedIn Icon" style={{ width: "25px", height: "25px" }} />
                                                    </div>
                                                );
                                            default:
                                                return;
                                    }
                                })
                            }
                        </PostedToIcons>
                    </div>
                </CardTableCell>
            </NoticeboardRow>
        );
    });
}


function PostTypeAssetPreview({ notice, handleCarouselIndexChange }) {
    const videoPending = notice.postType === "video" && notice.pendingMedia;
    return (
        <>
            {
                notice.postType === "text" &&
                <PreviewPlaceholderIcon
                    src={AttachIcon}
                    alt={AttachIcon} />
            }

            {
                notice.postType === "image" &&
                <PreviewMedia
                    src={getAssetUrl(notice)}
                    alt={getAssetUrl(notice)} />
            }

            {/* Render video content here */}
            {notice.postType === "video" && (
                videoPending ? (
                    <ProcessingPreview>
                        <span>Processing video...</span>
                        <div style={{ width: "30px", height: "30px", display: "flex", alignItems: "center" }}>
                            <LottieLoadingIndicator width="50px" height="50px" style={{ marginTop: "-8px" }} />
                        </div>
                    </ProcessingPreview>
                ) : (
                    <PreviewVideo
                        controls
                        poster={notice.video?.previewImageUrl || ""}
                    >
                        <source src={getAssetUrl(notice)} type="video/mp4" />
                        Your browser does not support the video tag.
                    </PreviewVideo>
                )
            )}

            { /* Render carousel content here */
                notice.postType === "carousel" &&
                <CarouselTableView items={notice.carousel} autoPlay={false} noticeUuid={notice.uuid} handleCarouselIndexChange={handleCarouselIndexChange} />
            }
        </>
    );
}
export default NoticeboardContent;
